<template>
  <div class="expense-investigation-onboarding">
    <progress-header
      :top-image-path="'header-red-square-top.svg'"
      :bottom-image-path="'header-red-square-bottom.svg'"
      :header-text="this.fixedHeader"
      :color-variant="'red'"
    />
    <div class="page-content">
        <template v-if="foundFixedExpenses">
          <expandable-transaction-check-list
                            :color-variant="'red'"
                            :title="'הוצאות קבועות'"
                            :is-expandable="false"
                            :toggleCheck="togglePredictedIsFixed">
            <template v-slot:subtitle>
              <div class="subtitle">
                המערכת זיהתה הוצאות שחזרו על עצמן בחודשים קודמים ולכן היא צופה שהן ימשיכו לקרות גם בעתיד.
                <span class="bold"> יש כאן הוצאה חד פעמית? </span>אפשר להסיר אותה.
              </div>
              <info-tool-tip
                  class="tool-tip-wrapper"
                  :text="'איך המערכת מחשבת מהן ההוצאות הקבועות שלך? '"
                  :popup-text="'אפשר לקרוא כאן.'"
                  :event-name="'ExpenseInvestigationTooltipClicked'"
                  :popup-component="expenseInvestigationHelpPopup"/>
            </template>
          </expandable-transaction-check-list>
          <div v-for="(expenses, category, index) in this.predictedExpenseQuestionsList" :key="category">
            <expandable-transaction-check-list
                              :color-variant="'red'"
                              :title="category"
                              :is-expandable="true"
                              :transaction-list="expenses"
                              :expand-on-create="index === 0"
                              :toggleCheck="togglePredictedIsFixed"/>
          </div>
        </template>
        <div v-if="foundFixedExpenses" class="empty-block-divider"></div>
        <separator v-if="foundFixedExpenses"/>
        <expandable-transaction-check-list
                          v-if="foundActualQuestions"
                          :isExpandable="foundFixedExpenses"
                          :color-variant="'red'"
                          :title="varibaleExpensesTitle"
                          :transaction-list="actualExpenseQuestionsList"
                          :toggleCheck="toggleActualIsFixed">
          <template v-slot:subtitle>
            <div v-if="!foundFixedExpenses" class="subtitle">
              המערכת זיהתה רק הוצאות שלא הולכות לחזור בחודשים הקרובים. אולי פספסנו משהו ו<span class="bold">יש כאן הוצאה קבועה?</span>
              אם כן, אפשר לסמן אותה.
            </div>
            </template>
        </expandable-transaction-check-list>
        <div class="button-container">
          <riseup-button class="done-btn"
                            :loading="isLoading"
                            :action="() => doneAnswering()"
                            :title="'סיימתי'"/>
        </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';
import BaseUI from 'base-ui';
import moneyUtils from '../../../../../../utils/money';
import consts from '../../../../../../constants/onboarding-state';
import ProgressHeader from '../../../../../../base-components/onboarding/ProgressHeader.vue';
import InfoToolTip from '../../../../../../base-components/onboarding/InfoToolTip.vue';
import ExpandableTransactionCheckList from '../../../../../../base-components/onboarding/ExpandableTransactionCheckList.vue';
import Segment from '../../../../../../Segment';
import ExpenseInvestigationHelpPopup
  from '../../../../../../base-components/onboarding/help-popups/ExpenseInvestigationHelpPopup.vue';

export default {
  name: 'ExpenseInvestigationOnboarding',
  components: {
    RiseupButton: BaseUI.RiseupButton,
    Separator: BaseUI.Separator,
    ProgressHeader,
    InfoToolTip,
    ExpandableTransactionCheckList,
  },
  async created() {
    await this.fetchExpenseQuestions();
    const numberOfFixedTransactions = this.predictedExpenseQuestions.filter(transaction => transaction.isFixed).length;
    const numberOfVariableTransactions = this.actualExpenseQuestions.length;
    Segment.trackUserGot('FixedExpensesReviewEntered', { numberOfFixedTransactions, numberOfVariableTransactions });
  },
  computed: {
    ...mapGetters('session', ['householdName']),
    ...mapState('expenseInvestigationQuestions', ['actualExpenseQuestions', 'predictedExpenseQuestions']),
    ...mapGetters('expenseInvestigationQuestions', ['predictedExpenseByCategory']),
    ...mapState('websocket', ['socketMessage']),
    ...mapState('onboardingState', ['isLoading']),
    expenseInvestigationHelpPopup() {
      return ExpenseInvestigationHelpPopup;
    },
    currentPageName() {
      return consts.ONBOARDING_PAGE.EXPENSE_INVESTIGATION;
    },
    predictedExpenseQuestionsList() {
      return _.mapValues(this.predictedExpenseByCategory, categoryList => _.map(categoryList, question => {
        return {
          businessName: question.businessName,
          amount: question.billingAmount,
          isChecked: question.isFixed,
          envelopeId: question.envelopeId,
        };
      }));
    },
    actualExpenseQuestionsList() {
      return _.map(this.actualExpenseQuestions, question => {
        return {
          businessName: question.businessName,
          amount: question.billingAmount,
          isChecked: question.isFixed,
          transactionId: question.transactionId,
        };
      });
    },
    foundActualQuestions() {
      return this.actualExpenseQuestions.length > 0;
    },
    foundFixedExpenses() {
      return this.predictedExpenseQuestions.length > 0;
    },
    fixedHeader() {
      return this.foundFixedExpenses
        ? `${this.householdName}, המערכת זיהתה את ההוצאות החודשיות שלך`
        : `${this.householdName}, המערכת זיהתה הוצאות`;
    },
    varibaleExpensesTitle() {
      return this.foundFixedExpenses
        ? 'אולי פספסנו משהו?'
        : 'לא זיהינו הוצאות קבועות בחשבון';
    },
  },
  methods: {
    ...mapActions('expenseInvestigationQuestions', ['fetchExpenseQuestions', 'saveExpenseInputs']),
    ...mapActions('onboardingState', ['nextPage']),
    ...mapMutations('expenseInvestigationQuestions', ['setPredictedIsFixed', 'setActualIsFixed']),
    ...mapMutations('onboardingState', ['setLoadingState']),
    async doneAnswering() {
      this.setLoadingState(true);
      const numberOfRemovedFixed = this.predictedExpenseQuestions.filter(transaction => !transaction.isFixed).length;
      const numberOfAddedVariables = this.actualExpenseQuestions.length;
      Segment.trackUserInteraction('FixedExpensesReviewNextClicked', { numberOfRemovedFixed, numberOfAddedVariables });
      await this.saveExpenseInputs();
    },
    formatAmount(amount) {
      return moneyUtils.formatAmountWithFraction(amount, 0);
    },
    togglePredictedIsFixed(question) {
      const isChecked = !question.isChecked;
      this.setPredictedIsFixed({ envelopeId: question.envelopeId, isFixed: isChecked });
    },
    toggleActualIsFixed(question) {
      const isChecked = !question.isChecked;
      this.setActualIsFixed({ transactionId: question.transactionId, isFixed: isChecked });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../../../../../node_modules/base-ui/src/scss/riseup-colors';
@import '../../../../../../scss/mixins';

.expense-investigation-onboarding {
  @include white-page-frame;
  height: 100%;

  .page-content {
    height: 100%;
    width: 100%;

    .tool-tip-wrapper {
      margin-bottom: 35px;
      width: 100%;
    }
    .subtitle {
      font-size: 18px;
      color: $riseup_black;
      margin-bottom: 35px;
      line-height: 1.44;
      .bold {
        font-weight: bold;
      }
    }

    .empty-block-divider {
      width: 100%;
      height: 60px;
      background-color: $riseup_v2_light_grey;
    }

    .button-container {
      @include responsive-padding;
      @include responsive-padding-top-bottom;

      .done-btn {
        width: 100%;
        white-space: nowrap;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/responsive-pages/authenticated/onboard2/steps/expense-investigation/ExpenseInvestigationOnboarding.vue